
import { Component, Input, inject, ChangeDetectionStrategy } from '@angular/core';
import { Router } from '@angular/router';
import { TranslatePipe } from '@ngx-translate/core';
import { AdviceTodayResponse } from '@TheGreenHouseCompany/vegiwise-sdk-typescript/dist/generated/@dto/responses/advice/advice-today.response';
import { AdviceTrigger } from '@TheGreenHouseCompany/vegiwise-sdk-typescript/dist/generated/@enums/advice-trigger.enum';
import routesConstants from '../../../@commons/constants/routes.constants';
import { LocationLabelPipe } from '../../../@commons/pipes/location-label.pipe';

/** Ce que la plante attend, et la couleur qui va avec. */
interface Need {
  /** Clé i18n du besoin, sous `home.attention.needs`. */
  key: string;
  background: string;
  ink: string;
  emoji: string;
}

/**
 * Le besoin déduit du déclencheur.
 *
 * Un seul mot par carte : c'est la règle de ce carrousel. Une vignette qui
 * annonce quatre choses ne se lit pas d'un coup d'œil, et c'est précisément ce
 * qu'on lui demande.
 */
const NEED_BY_TRIGGER: Record<string, Need> = {
  [AdviceTrigger.FROST]: { key: 'protect', background: '#E0F2FE', ink: '#075985', emoji: '❄️' },
  [AdviceTrigger.HEATWAVE]: { key: 'water', background: '#E1EFFE', ink: '#1A4166', emoji: '💧' },
  [AdviceTrigger.DRY_SPELL]: { key: 'water', background: '#E1EFFE', ink: '#1A4166', emoji: '💧' },
  [AdviceTrigger.HEAVY_RAIN]: { key: 'watch', background: '#FEF3C7', ink: '#92400E', emoji: '⚠️' },
  [AdviceTrigger.STRONG_WIND]: { key: 'watch', background: '#FEF3C7', ink: '#92400E', emoji: '⚠️' },
};

const LUNAR_NEED: Need = { key: 'lunar', background: '#E4E1FE', ink: '#261E77', emoji: '🌙' };
const HARVEST_NEED: Need = { key: 'harvest', background: '#DAF2E0', ink: '#1C5C41', emoji: '✅' };

/** Une carte du carrousel. */
export interface AttentionCard {
  plantUid: string;
  cultivationUid: string;
  variantUid: string;
  name: string;
  locationName: string | null;
  imageUrl: string | null;
  need: Need;
}

/**
 * « Ces plantes ont besoin de toi » : les cultures concernées aujourd'hui, en
 * images.
 *
 * Il remplace la grille de TOUTES les cultures, qui faisait de l'accueil une
 * seconde version du Potager. Ici, une plante n'apparaît que si la journée lui
 * demande quelque chose — et la carte le dit en un mot.
 *
 * Aucun appel : tout vient du `advice/today` déjà chargé par l'accueil.
 */
@Component({
  selector: 'vw-plants-attention-carousel',
  standalone: true,
  imports: [TranslatePipe, LocationLabelPipe],
  templateUrl: './plants-attention-carousel.component.html',
  // La barre de defilement horizontale n'apporte rien sur mobile et coupe la
  // rangee de cartes en deux.
  changeDetection: ChangeDetectionStrategy.Eager,
  styles: [
    `:host ::-webkit-scrollbar { display: none; }
     :host div { scrollbar-width: none; }`,
  ],
})
export class PlantsAttentionCarouselComponent {
  @Input() advice: AdviceTodayResponse | null = null;

  private readonly router = inject(Router);

  /**
   * Les récoltes d'abord, puis les conseils, une carte par plante.
   *
   * Une plante peut être concernée par plusieurs déclencheurs le même jour ;
   * elle ne mérite pas deux vignettes pour autant. La première rencontrée
   * gagne, et l'ordre fait que c'est la plus gratifiante.
   */
  get cards(): AttentionCard[] {
    const seen = new Set<string>();
    const cards: AttentionCard[] = [];

    for (const harvest of this.advice?.harvestSuggestions ?? []) {
      if (seen.has(harvest.plantUid)) continue;
      seen.add(harvest.plantUid);
      cards.push({
        plantUid: harvest.plantUid,
        cultivationUid: harvest.cultivationUid,
        variantUid: harvest.variantUid,
        name: harvest.name,
        locationName: harvest.locationName,
        imageUrl: harvest.imageUrl,
        need: HARVEST_NEED,
      });
    }

    for (const card of this.advice?.advices ?? []) {
      if (seen.has(card.plantUid)) continue;
      seen.add(card.plantUid);
      cards.push({
        plantUid: card.plantUid,
        cultivationUid: card.cultivationUid,
        variantUid: card.variantUid,
        name: card.plantName,
        locationName: card.locationName,
        imageUrl: card.imageUrl,
        need: `${card.trigger}`.startsWith('LUNAR_')
          ? LUNAR_NEED
          : (NEED_BY_TRIGGER[card.trigger] ?? LUNAR_NEED),
      });
    }

    return cards;
  }

  /**
   * La fiche de la plante, quand on sait la construire.
   *
   * La route demande culture + variété + plante ; sans les trois, mieux vaut
   * le potager qu'une URL cassée.
   */
  open(card: AttentionCard): void {
    if (card.cultivationUid && card.variantUid && card.plantUid) {
      void this.router.navigate([
        `/garden/cultivations/${card.cultivationUid}/variants/${card.variantUid}/plants/${card.plantUid}`,
      ]);
      return;
    }
    void this.router.navigate([routesConstants.GARDEN_LIST]);
  }

  seeAll(): void {
    void this.router.navigate([routesConstants.GARDEN_LIST]);
  }
}
